import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowLeft, Palmtree, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (!token) {
      toast.error('Token manquant');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.password
      });
      
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-[#005F73] mb-4">Lien invalide</h2>
          <p className="text-stone-600 mb-6">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Button onClick={() => navigate('/forgot-password')}>
            Demander un nouveau lien
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#0A9396] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" strokeWidth={2} />
          </div>
          <h2 className="text-3xl font-semibold text-[#005F73] mb-4">
            Mot de passe réinitialisé !
          </h2>
          <p className="text-stone-600 mb-6">
            Vous allez être redirigé vers la page de connexion...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palmtree className="w-10 h-10 text-[#005F73]" strokeWidth={1.5} />
            <span className="text-2xl font-semibold text-[#005F73]">GwadImmo Invest</span>
          </div>
          <h1 className="text-3xl font-semibold text-[#005F73] mb-2">
            Nouveau mot de passe
          </h1>
          <p className="text-stone-600">Choisissez un nouveau mot de passe sécurisé</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-password"
                  id="password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
              <p className="text-xs text-stone-500 mt-1">Minimum 6 caractères</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-confirm-password"
                  id="confirmPassword"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <Button
              data-testid="btn-reset"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#005F73] hover:bg-[#005F73]/90 text-lg font-medium"
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            Vous vous souvenez de votre mot de passe ?{' '}
            <Link to="/login" className="text-[#005F73] font-medium hover:underline">
              Se connecter
            </Link>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-stone-600 hover:text-[#005F73]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
}
